import { PACKAGING_TYPES } from 'constants';
import _isEmpty from 'lodash/isEmpty';
import { getSelectedValue, phoneToNumber } from './commonUtils';
import { convertToShortType } from './cardTypeUtils';

const isGiftWrappable = (products) => products.every((product) => product.isGiftWrappable);

export const transformProductsForOrderReview = (products = []) => {
  const isOrderWrappable = isGiftWrappable(products);

  return products.map((product) => ({
    offerItemId: product.offerItemId,
    cmosSku: product.cmosskuId,
    quantity: product.quantity,
    price: product.price.adjustedPrice || product.price.currentPrice,
    isGiftWrap: isOrderWrappable && product.giftOptions.giftWrapCode === PACKAGING_TYPES.GIFT_PACKAGING,
  }));
};

/*
  Get Data for Order Review Request.
 */
export const mapDataForOrderReview = (clientsModule) => {
  const { conciergeProducts } = clientsModule.cart;
  const zipCode = getSelectedValue(clientsModule.clientDetails.data?.shippingAddress).zip;
  const { keys, lastApplied } = clientsModule.checkout.promo;
  const promo = [...keys, ...lastApplied];

  const items = transformProductsForOrderReview(conciergeProducts);

  return {
    promo,
    zipCode,
    items,
  };
};

const parseToNumber = (val) => parseFloat(val?.toString().replace('$', '') || 0);

const mapBopsAddress = (bopsAddress) => ({
  line1: bopsAddress.line1,
  line2: bopsAddress.line2,
  zip: bopsAddress.zip,
  country: bopsAddress.country || 'US',
  city: bopsAddress.city,
  state: bopsAddress.state,
  contactAddressName: bopsAddress.firstName,
});

/** Calculate amount for paymnents */
export const getAmountReceived = (orderTotalPrice, giftCardPayments = []) => {
  const orderTotalAmt = parseToNumber(orderTotalPrice);
  const gcAmountReceived = giftCardPayments.reduce((acc, obj) => acc + parseToNumber(obj.giftCardBalance), 0);
  const amountReceived = orderTotalAmt - gcAmountReceived;
  return amountReceived < 0 ? '0.00' : amountReceived.toFixed(2);
};

/** Calculate amount for gift cards */
export const getGCPayments = (orderTotalPrice, giftCardPayments = []) => {
  let total = parseToNumber(orderTotalPrice);
  return giftCardPayments.map(({ cardType, giftCardNumber, giftCardPin, giftCardBalance }) => {
    const gcBalance = parseToNumber(giftCardBalance);
    const gcAmount = total - gcBalance >= 0 ? gcBalance : total;
    total = total - gcBalance > 0 ? total - gcBalance : 0;
    return {
      cardType,
      giftCardNumber,
      giftCardPin,
      amountReceived: gcAmount.toFixed(2),
    };
  });
};

/*
  Get Data for Order Submit Request.
 */
export const mapDataForOrderSubmit = ({
  clientData,
  conciergeProducts,
  shipmentMethod,
  promo,
  gcPayments,
  deliveryNumber,
  bopsAddress,
}) => {
  const shipAddr = getSelectedValue(clientData.shippingAddress);
  const payment = clientData.payments.find((p) => p.selected) || clientData.payments.find((p) => p.defaultCard) || {};
  const isOrderWrappable = isGiftWrappable(conciergeProducts);
  const isBopsOrder = !_isEmpty(bopsAddress);

  const items = conciergeProducts.map((product, index) => {
    const isPriceAdjusted = !!product.price?.adjustedPrice;
    const inStock = !!(product.bopsAvailability && product.bopsAvailability[Number(bopsAddress.storeNum || 0)]);
    const mappedItem = {
      offerItemId: product.offerItemId,
      cmosSku: product.cmosskuId,
      pimSku: product.itemskuId,
      quantity: product.quantity,
      shipmentOption: shipmentMethod.id,
      currencyCode: product.price.currencyCode,
      itemPriceEach: product.price.itemPriceExt,
      itemTaxEach: shipmentMethod.itemTaxes[index] || product.price.itemTax,
      freightEach: shipmentMethod.lineDeliveryCharges[index],
      otherEach: shipmentMethod.giftWrappingCharges[index] || '0.00',
      adjustedPriceEach: product.price.adjustedPrice || product.price.currentPrice,
      isIce: product.isIce,
      productName: product.name,
      productImageUrl: product.imageUrl || '',
      designerName: product.brand,
      color: product.color,
      size: product.size,
      departmentCode: product.departmentCode,
      ...(isOrderWrappable && {
        isGiftWrap: product.giftOptions?.giftWrapCode === PACKAGING_TYPES.GIFT_PACKAGING,
        giftmessage1: product.giftOptions?.giftMessage1,
        giftmessage2: product.giftOptions?.giftMessage2,
        giftmessage3: product.giftOptions?.giftMessage3,
        giftmessage4: product.giftOptions?.giftMessage4,
        giftmessage5: product.giftOptions?.giftMessage5,
      }),
      promotionCodes: product.promotionsApplied,
      promotionDiscAmt: product.promotionDiscAmt,
      ...(isPriceAdjusted && {
        priceOverrideReason: product.price?.priceOverrideReason,
        priceOverrideReasonDetails: product.price?.priceOverrideReasonDetails,
      }),
      createdBy: product.createdBy,
      ...(isBopsOrder && {
        storePickupFlag: 'true',
        ...(inStock ? { pickupStoreNumber: bopsAddress.storeNum } : { shiptoStoreNumber: bopsAddress.storeNum }),
      }),
    };

    return mappedItem;
  });

  const shippingPhones = [
    {
      type: 'DayPhone',
      number: phoneToNumber(shipAddr.phone),
    },
  ];

  if (deliveryNumber) {
    shippingPhones.push({
      type: 'DeliveryPhone',
      number: phoneToNumber(deliveryNumber),
    });
  }

  return {
    orderTotalAmt: shipmentMethod.orderTotalAmt.replace('$', ''),
    orderTotalTax: shipmentMethod.orderTotalTax.replace('$', ''),
    deliveryCharges: shipmentMethod.deliveryCharges.replace('$', ''),
    promotionsApplied: promo.codes,
    soldTo: {
      firstName: clientData.firstName,
      lastName: clientData.lastName,
      email: clientData.email,
      profileId: clientData.clientId,
      address: payment.billingAddress || shipAddr,
      phone: [
        {
          type: 'DayPhone',
          number: phoneToNumber(payment.billingAddress?.phone || shipAddr.phone),
        },
      ],
    },
    shipTo: [
      {
        firstName: !isBopsOrder ? shipAddr.firstName : clientData.firstName,
        lastName: !isBopsOrder ? shipAddr.lastName : clientData.lastName,
        phone: shippingPhones,
        address: !isBopsOrder ? shipAddr : mapBopsAddress(bopsAddress),
        items,
      },
    ],
    payments: _isEmpty(payment)
      ? []
      : [
          {
            cardType: convertToShortType(payment.networkType),
            keyIdentifier: payment.keyIdentifier,
            expiryMonth: Number(payment.cardExpMonth) || 0,
            expiryYear: Number(payment.cardExpYear) || 0,
            billingAddress: {
              ...payment.billingAddress,
              phone: [
                {
                  type: 'DayPhone',
                  number: phoneToNumber(payment.billingAddress.phone),
                },
              ],
            },
            amountReceived: getAmountReceived(shipmentMethod.orderTotalAmt, gcPayments),
          },
        ],
    gcPayments: getGCPayments(shipmentMethod.orderTotalAmt, gcPayments),
  };
};

export const mapDataForStoreInfo = (storeDetails, storeNum) => {
  const { name, addressDetails } = storeDetails;
  const { addressLine1, addressLine2, city, state, postalCode } = addressDetails?.[0];
  return {
    firstName: name,
    line1: addressLine1,
    line2: addressLine2,
    zip: postalCode,
    country: '',
    city,
    state,
    storeNum,
  };
};

export const mapBopsAvailability = (product) => {
  const { webProductSku, skuStores } = product;
  const skuId = webProductSku?.match(/\/(.*)/);
  let bopsAvailability = {};
  if (skuId) {
    bopsAvailability = skuStores
      ?.find((val) => val.skuId === skuId[1])
      ?.storeInventories?.reduce(
        (acc, curr) => (Number(curr.bopsQty) ? { ...acc, [Number(curr.storeNo)]: curr.bopsQty } : { ...acc }),
        {},
      );
  }

  return {
    ...product,
    bopsAvailability,
  };
};
